import { useEffect, useState } from "react";
import { useTonWallet } from "@tonconnect/ui-react";
import { rpcCall } from "../ws/client";
import { Order } from "../ws/types";
import { toast } from "./ui/Toast";
import { Button } from "./ui";
import "../styles/MyOrders.scss";

export default function MyOrders() {
    const wallet = useTonWallet();
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState<"all" | "active" | "completed" | "cancelled">("active");
    const [tradingPaused, setTradingPaused] = useState(false);

    useEffect(() => {
        if (wallet) {
            loadOrders();
            loadTradingStatus();
        }
    }, [wallet, filter]);

    const loadTradingStatus = async () => {
        if (!wallet) return;
        
        try {
            const response = await rpcCall<{ wallet: string }, { trading_paused: boolean }>(
                "user.get_trading_status",
                { wallet: wallet.account.address }
            );
            
            if (response.result) {
                setTradingPaused(response.result.trading_paused);
            }
        } catch (err) {
            console.error("Failed to load trading status:", err);
        }
    };

    const loadOrders = async () => {
        if (!wallet) return;

        setLoading(true);
        try {
            const response = await rpcCall<any, { orders: Order[] }>(
                "orders.list",
                {
                    creator: wallet.account.address,
                    status: filter === "all" ? undefined : filter
                }
            );

            if (response.result) {
                setOrders(response.result.orders);
            }
        } catch (err) {
            console.error("Failed to load orders:", err);
            toast.error("Не удалось загрузить ордера");
        } finally {
            setLoading(false);
        }
    };

    const handleCancelOrder = async (orderId: string) => {
        if (!wallet) return;

        if (!confirm("Вы уверены, что хотите отменить этот ордер?")) {
            return;
        }

        try {
            const response = await rpcCall(
                "orders.cancel",
                {
                    order_id: orderId,
                    wallet: wallet.account.address
                }
            );

            if (response.error) {
                toast.error(response.error.message);
            } else {
                toast.success("Ордер отменён");
                loadOrders();
            }
        } catch (err) {
            console.error(err);
            toast.error("Ошибка при отмене ордера");
        }
    };

    if (!wallet) {
        return (
            <div className="my-orders">
                <div className="wallet-warning">
                    <div className="warning-icon">🔐</div>
                    <div className="warning-title">Подключите кошелёк</div>
                    <div className="warning-text">Чтобы увидеть свои ордера, необходимо подключить TON кошелёк</div>
                </div>
            </div>
        );
    }

    return (
        <div className="my-orders">
            <div className="list-header">
                <h2>Мои ордера</h2>
                <div className="filters">
                    <button 
                        className={`filter-btn ${filter === "all" ? "active" : ""}`}
                        onClick={() => setFilter("all")}
                    >
                        Все
                    </button>
                    <button 
                        className={`filter-btn ${filter === "active" ? "active" : ""}`}
                        onClick={() => setFilter("active")}
                    >
                        Активные
                    </button>
                    <button 
                        className={`filter-btn ${filter === "completed" ? "active" : ""}`}
                        onClick={() => setFilter("completed")}
                    >
                        Завершённые
                    </button>
                    <button 
                        className={`filter-btn ${filter === "cancelled" ? "active" : ""}`}
                        onClick={() => setFilter("cancelled")}
                    >
                        Отменённые
                    </button>
                </div>
            </div>

            {tradingPaused && filter === "active" && (
                <div className="trading-paused-notice">
                    <div className="notice-icon">🔕</div>
                    <div className="notice-content">
                        <div className="notice-title">Торги приостановлены</div>
                        <div className="notice-text">
                            Ваши активные ордера скрыты от других пользователей. 
                            Возобновите торги внизу страницы, чтобы они снова стали видны.
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loading-container">
                    <div className="spinner" />
                    <span className="loading-text">Загрузка...</span>
                </div>
            ) : orders.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">📝</div>
                    <div className="empty-state-title">У вас нет ордеров</div>
                    <div className="empty-state-description">
                        Создайте новый ордер, чтобы начать торговлю
                    </div>
                </div>
            ) : (
                <div className="orders-grid">
                    {orders.map((order) => (
                        <div key={order.id} className={`order-card ${order.side} status-${order.status}`}>
                            <div className="order-header">
                                <span className={`badge badge-${order.side}`}>
                                    {order.side === "buy" ? "Покупка" : "Продажа"}
                                </span>
                                <span className={`status-badge status-${order.status}`}>
                                    {order.status === "active" ? "Активен" :
                                     order.status === "in_progress" ? "В процессе" :
                                     order.status === "pending_acceptance" ? "Есть заявки" :
                                     order.status === "completed" ? "Завершён" : "Отменён"}
                                </span>
                            </div>

                            <div className="order-body">
                                <div className="amount-row">
                                    <span className="amount">{order.amount}</span>
                                    <span className="token-symbol"> {order.jetton_symbol}</span>
                                </div>
                                <div className="price-row">
                                    <span className="price">@ {order.price} {order.fiat_currency}</span>
                                </div>
                                <div className="total-row">
                                    <span className="total">Итого: </span>
                                    <span className="total-amount">
                                        <strong>{order.fiat_amount.toFixed(2)}</strong> {order.fiat_currency}
                                    </span>
                                </div>
                            </div>

                            <div className="order-footer">
                                <div className="payment-methods">
                                    {order.payment_methods.map(method => (
                                        <span key={method} className="payment-badge">
                                            {method}
                                        </span>
                                    ))}
                                </div>
                                
                                {order.status === "pending_acceptance" && (
                                    <div className="pending-alert">
                                        📬 У вас есть заявки на этот ордер!
                                    </div>
                                )}
                                
                                {order.status === "active" && (
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleCancelOrder(order.id)}
                                    >
                                        Отменить
                                    </Button>
                                )}
                                
                                {order.counterparty && (
                                    <div className="counterparty">
                                        Контрагент: <code>{order.counterparty.slice(0, 8)}...{order.counterparty.slice(-6)}</code>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
